<script setup lang="ts">
import {
  ContainerTypes,
  Contexts,
  CurrentCaseServiceInterface,
  Helpers,
  NamedBindings,
  TranslationService,
  getContainer,
} from '@dandelion/core';
import { PropType, ref, watchEffect } from 'vue';
import { Preferences } from '../enum/Preferences';
import { NamedServices } from '../enum/NamedServices';
import { PreferencesService } from '../services/PreferencesService';
import { debounce } from 'lodash';

const props = defineProps({
  consent: {
    type: String as PropType<string>,
    default: 'exempt',
  },
  exemptMotivation: {
    type: String as PropType<string>,
    default: '',
  }
});

//necessary because a prop cannot be used for v-model
let selectedConsent = ref(props.consent);
let consentExemptMotivation = ref(props.exemptMotivation);

watchEffect(() => {
  selectedConsent.value = props.consent;
  consentExemptMotivation.value = props.exemptMotivation;
});

const container = getContainer(Contexts.DEFAULT);
const ts = getContainer(Contexts.DEFAULT).get(
  NamedBindings.TranslationService
) as TranslationService;
const newCurrentCaseService = getContainer(
  Contexts.DEFAULT,
  ContainerTypes.NEW_CASE
).get(
  NamedBindings.CurrentCaseServiceForNewCase
) as CurrentCaseServiceInterface;
const centrePreferencesService = container.get(
  NamedServices.CentrePreferencesService
) as PreferencesService;
const askConsentValue = (
  await centrePreferencesService.getPreference(Preferences.ASKCONSENT)
).value;

const emit = defineEmits<{ (e: 'setConsent', consent: string, newConsentExemptMotivation: string): void }>();
const debouncedEmit = debounce(() => emit('setConsent', selectedConsent.value, consentExemptMotivation.value), 200);

function setConsent() {
  if (newCurrentCaseService.isCaseOpened) {
    const model = newCurrentCaseService.getModel();
    Helpers.setObjByPath('consent', model, selectedConsent.value);
    if (selectedConsent.value == 'exempt') {
      Helpers.setObjByPath('consentExemptMotivation', model, consentExemptMotivation.value);
    }
  }
  debouncedEmit();
}
setConsent();
</script>

<template>
  <div v-if="askConsentValue !== 'false'">
    <label
      id=" consent-choice-label"
      class="variableLabel"
      v-html="ts.t('Consent')"
    ></label>
    <select
      id="consent-choice"
      v-model="selectedConsent"
      @change="setConsent()"
    >
      <option value="yes" v-html="ts.t('Yes')"></option>
      <option value="no" v-html="ts.t('No')"></option>
      <option value="exempt" v-html="ts.t('Exempt')"></option>
    </select>
  </div>
  <div id="consent-motivation" v-if="selectedConsent == 'exempt' && askConsentValue !== 'false'">
    <label class="variableLabel" v-html="ts.t('Motivation for consent exempt')"></label>
    <textarea v-model="consentExemptMotivation" @input="setConsent()"></textarea>
  </div>
</template>

<style>
  #consent-motivation label {
    max-width: 12rem;
    text-wrap: balance;
  }
</style>